// pages/RuleDetail.jsx
import { useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, RotateCcw, Pencil, FileText } from "lucide-react";
import { useRuleByID } from "hooks/useRuleById";
import Skeleton from "components/Skeleton";
import ConfirmDialog from "components/ConfirmDialog";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import PageHeader from "components/PageLayout";

export default function RuleDetail() {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { rules, loading, error } = useRuleByID(ruleId);
  const { toast } = useToast();
  const companyId = useEffectiveCompanyId();

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", description: "", onConfirm: () => {} });
  const closeConfirmDialog = useCallback(() => setConfirmDialog(c => ({ ...c, open: false })), []);

  function handleRollback(targetVersion) {
    setConfirmDialog({
      open: true,
      title: "Rollback Rule",
      description: `Rollback to version ${targetVersion}?`,
      confirmLabel: "Rollback",
      variant: "destructive",
      onConfirm: async () => {
        closeConfirmDialog();
        try {
          const res = await makeApiRequest(
            "post",
            apiRoutes.rollbackRule(ruleId),
            { id: user.id, accessToken: user.accessToken },
            companyId,
            { targetVersion }
          );

          toast({
            status: "success",
            title: "Rollback complete",
            description: `Rolled back to v${res.data.rolledBackFromVersion}, now Live as v${res.data.newVersion.version}.`,
            action: {
              label: "Refresh",
              onClick: () => window.location.reload(),
            },
          });
        } catch (err) {
          toast({
            status: "destructive",
            title: "Rollback failed",
            description: "Could not rollback this rule. Please try again.",
          });
        }
      },
    });
  }

  if (loading) {
    return (
      <div className="container py-6 space-y-6">
        <Skeleton className="h-8 w-1/3 mb-4" />
        <Skeleton className="h-6 w-2/3 mb-2" />
        <Skeleton className="h-6 w-1/2" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-6">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-ghost inline-flex items-center gap-2 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <p className="text-red-600">
          Failed to load rule details. Please try again later.
        </p>
      </div>
    );
  }

  if (!rules || rules.length === 0) {
    return (
      <div className="container py-6">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-ghost inline-flex items-center gap-2 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <p>No rule found with ID {ruleId}.</p>
      </div>
    );
  }

  // Latest version is first (API/hook should return sorted)
  const latest = rules[0];

  const renderConstraints = (constraints) => {
    if (!constraints || constraints.length === 0) return "—";
    return constraints.map((c, idx) => (
      <span
        key={idx}
        className="inline-block px-2 py-0.5 mr-1 mb-1 text-xs rounded bg-muted text-muted-foreground"
      >
        {c.type}: {c.value}
      </span>
    ));
  };

  const renderLogs = (logs) => {
    if (!logs || logs.length === 0)
      return <p className="text-sm text-muted-foreground">No logs found.</p>;
    return (
      <ul className="mt-2 space-y-2">
        {logs.map((log) => (
          <li
            key={log.logId}
            className="text-sm flex justify-between items-center"
          >
            <div>
              <p className="font-medium">{log.title}</p>
              <p className="text-muted-foreground">
                {log.createdAt ? new Date(log.createdAt).toLocaleString() : "—"}{" "}
                {log.implementer?.name && <>by {log.implementer.name}</>}
              </p>
            </div>
            <Link
              to={`/audit/${log.logId}`}
              className="btn btn-ghost btn-xs flex items-center gap-1"
            >
              <FileText className="h-3 w-3" />
              View Log
            </Link>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <PageHeader
        title={`Rule ${latest.name}`}
        subtitle="View rule configuration history and make changes."
        actions={
          <div className="flex gap-2">
            <Link
              to={`/rules/${latest.ruleId}/edit`}
              className="btn btn-primary ml-4"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Link>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-ghost inline-flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
        }
        badge={latest.status}
      />
      <div className="container space-y-6">
        {/* Latest Version Details */}
        <div className="surface rounded-xl p-6 ring-1 ring-border space-y-3">
          <p>
            <strong>Version:</strong> v{latest.version}
          </p>
          <p>
            <strong>Created:</strong>{" "}
            {latest.createdAt
              ? new Date(latest.createdAt).toLocaleString()
              : "—"}
          </p>
          <p>
            <strong>Last Updated:</strong>{" "}
            {latest.updatedAt
              ? new Date(latest.updatedAt).toLocaleString()
              : "—"}
          </p>
          <p>
            <strong>Bulk Operation ID:</strong> {latest.bulkOpId ?? "—"}
          </p>
          <p>
            <strong>Constraints:</strong>{" "}
            {renderConstraints(latest.constraints)}
          </p>
          <p>
            <strong>Routing:</strong>{" "}
            {latest.routing
              ?.map((r) => `${r.mid} (${r.allocation}%)`)
              .join(", ") ?? "—"}
          </p>

          {/* Logs for latest version */}
          <div className="mt-4">
            <h3 className="text-md font-semibold">Logs</h3>
            {renderLogs(latest.logs)}
          </div>
        </div>

        {/* Version History */}
        {/* Version History */}
        {rules.length > 1 && (
          <div className="surface rounded-xl p-6 ring-1 ring-border">
            <h2 className="text-lg font-semibold mb-4">Version History</h2>
            <ul className="space-y-3">
              {rules.map((ver, idx) => (
                <li
                  key={ver.version}
                  className="border-b border-border pb-3 last:border-0 last:pb-0"
                >
                  <div className="flex items-start justify-between w-full">
                    {/* Left side: version details */}
                    <div className="flex-1">
                      <p className="font-medium">
                        v{ver.version}
                        {ver.isLatest && " – Live"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Updated:{" "}
                        {ver.updatedAt
                          ? new Date(ver.updatedAt).toLocaleString()
                          : "—"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Constraints: {renderConstraints(ver.constraints)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Routing:{" "}
                        {ver.routing
                          ?.map((r) => `${r.mid} (${r.allocation}%)`)
                          .join(", ") ?? "—"}
                      </p>

                      {/* Show log details inline, but no buttons here */}
                      {ver.logs && ver.logs.length > 0 && (
                        <div className="mt-2">
                          <h4 className="text-sm font-semibold">Logs</h4>
                          <ul className="mt-1 space-y-1">
                            {ver.logs.map((log) => (
                              <li key={log.logId} className="text-sm">
                                <p className="font-medium">{log.title}</p>
                                <p className="text-muted-foreground">
                                  {log.createdAt
                                    ? new Date(log.createdAt).toLocaleString()
                                    : "—"}{" "}
                                  {log.implementer?.name && (
                                    <>by {log.implementer.name}</>
                                  )}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Right side: actions */}
                    <div className="flex flex-col gap-3 items-end">
                      {/* View log buttons */}
                      {ver.logs &&
                        ver.logs.map((log) => (
                          <Link
                            key={log.logId}
                            to={`/audit/${log.logId}`}
                            className="btn btn-secondary flex items-center gap-1"
                          >
                            <FileText className="h-4 w-4" />
                            View Log
                          </Link>
                        ))}
                      {/* Rollback button */}
                      {idx !== 0 && (
                        <button
                          className="btn btn-ghost flex items-center gap-1 h-fit"
                          onClick={() => handleRollback(ver.version)}
                        >
                          <RotateCcw className="h-4 w-4" />
                          Rollback to v{ver.version}
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <ConfirmDialog {...confirmDialog} onCancel={closeConfirmDialog} />
    </>
  );
}
